import { Icon } from '@iconify/react';
import { format, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Funnel,
	FunnelChart,
	LabelList,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';
import type { VerificationAnalytics as VerificationAnalyticsData } from '../../../types/ApiTypes';
import SkeletonCard from '../../components/SkeletonCard';
import { useApi } from '../../hooks/useApi';
import Dashboard from '../../layout/Dashboard';
import {
	Card,
	CardsGrid,
	Content,
	Heading,
	HeadingSub,
	MetaMuted,
	StatTitle,
	StatValue,
} from '../../styles/OverviewStyle';
import { formatCompactNumber, formatWithCommas } from '../../utils/numberHelpers';

const COLORS = {
	bvnAndNin: '#22c55e',
	bvnOnly: '#3b82f6',
	ninOnly: '#8b5cf6',
	emailOnly: '#eab308',
	nothingVerified: '#6b7280',
	verified: '#22c55e',
	unverified: '#f97316',
	blocked: '#ef4444',
};

const formatMonth = (m: string) => {
	try {
		return format(parse(m, 'yyyy-MM', new Date()), 'MMM yyyy');
	} catch {
		return m;
	}
};

const formatTxType = (t: string) =>
	t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const pct = (val: number, total: number) =>
	total > 0 ? ((val / total) * 100).toFixed(1) : '0.0';

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
	<h2 className="text-[15px] font-semibold text-[#101018] mb-4 mt-8">{children}</h2>
);

interface MiniCardProps { title: string; value: string; sub?: string; accent?: string }
const MiniCard = ({ title, value, sub, accent }: MiniCardProps) => (
	<Card>
		<StatTitle>{title}</StatTitle>
		<StatValue style={{ fontSize: 26 }}>{value}</StatValue>
		{sub && <MetaMuted change={false} style={{ color: accent ?? '#9C9C9F' }}>{sub}</MetaMuted>}
	</Card>
);

export default function VerificationAnalytics(): React.JSX.Element {
	const { analytics: analyticsService } = useApi();
	const [data, setData] = useState<VerificationAnalyticsData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

	const fetchData = async (start?: string, end?: string) => {
		setLoading(true);
		setError(null);
		try {
			const params: Record<string, string> = {};
			if (start) params.startDate = start;
			if (end) params.endDate = end;
			const res = await analyticsService.getVerificationAnalytics(params);
			if (res.status === 'success' && res.data) {
				setData(res.data);
			} else {
				setError('Failed to load analytics');
			}
		} catch {
			setError('Failed to load analytics');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => { fetchData(); }, []);

	const handleApply = () => fetchData(dateRange.startDate, dateRange.endDate);

	const exportCSV = () => {
		if (!data) return;
		const vb = data.verificationBreakdown;
		const rows = [
			['Metric', 'Value'],
			['Total Users', vb.total],
			['Fully Verified', vb.fullyVerified],
			['Unverified', vb.unverified],
			['Blocked', vb.blocked],
			['Verification Rate', `${vb.verificationRate}%`],
			['Total Transactions', data.transactionStats.totalTransactions],
			['Total Volume', data.transactionStats.totalVolume],
		];
		const csv = rows.map(r => r.join(',')).join('\n');
		const blob = new Blob([csv], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'verification-analytics.csv';
		a.click();
		URL.revokeObjectURL(url);
	};

	const renderContent = () => {
		if (loading) {
			return (
				<CardsGrid>
					{[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}
				</CardsGrid>
			);
		}

		if (error || !data) {
			return (
				<div className="flex flex-col items-center justify-center py-24 gap-3">
					<Icon icon="solar:danger-triangle-linear" width={40} className="text-[#ef4444]" />
					<p className="text-sm text-[#6D6D72]">{error ?? 'No data available'}</p>
					<button
						onClick={() => fetchData()}
						className="text-xs px-4 py-2 border border-[#E7E7E9] rounded-lg hover:bg-[#F9F9FA] transition-colors"
					>
						Retry
					</button>
				</div>
			);
		}

		const { verificationBreakdown: vb, registrationTrend, verificationFunnel, transactionStats, blockedStats } = data;

		const donutData = [
			{ name: 'BVN + NIN', value: vb.byVerificationType.bvnAndNin, color: COLORS.bvnAndNin },
			{ name: 'BVN Only', value: vb.byVerificationType.bvnOnly, color: COLORS.bvnOnly },
			{ name: 'NIN Only', value: vb.byVerificationType.ninOnly, color: COLORS.ninOnly },
			{ name: 'Email Only', value: vb.byVerificationType.emailOnly, color: COLORS.emailOnly },
			{ name: 'Nothing Verified', value: vb.byVerificationType.nothingVerified, color: COLORS.nothingVerified },
		];

		const stepData = [
			{ name: 'Email Verified', count: vb.byStep.emailVerified },
			{ name: 'Phone Verified', count: vb.byStep.phoneVerified },
			{ name: 'BVN Verified', count: vb.byStep.bvnVerified },
			{ name: 'NIN Verified', count: vb.byStep.ninVerified },
		];

		const maxDropOff = Math.max(...verificationFunnel.map(f => parseFloat(f.dropOffRate)));

		const trendData = registrationTrend.map(r => ({
			...r,
			month: formatMonth(r.month),
		}));

		const txStatusData = [
			{
				name: 'Verified',
				count: transactionStats.byVerificationStatus.verifiedUsers.count,
				volume: parseFloat(transactionStats.byVerificationStatus.verifiedUsers.volume),
			},
			{
				name: 'Unverified',
				count: transactionStats.byVerificationStatus.unverifiedUsers.count,
				volume: parseFloat(transactionStats.byVerificationStatus.unverifiedUsers.volume),
			},
		];

		const txTypeData = transactionStats.byType.map(t => ({
			name: formatTxType(t.type),
			volume: parseFloat(t.volume),
			count: t.count,
		}));

		return (
			<>
				{/* Section 1 — Summary Cards */}
				<CardsGrid>
					<MiniCard title="Total Users" value={formatWithCommas(vb.total)} />
					<MiniCard
						title="Fully Verified"
						value={formatWithCommas(vb.fullyVerified)}
						sub={`${vb.verificationRate}% verification rate`}
						accent={COLORS.verified}
					/>
					<MiniCard title="Unverified" value={formatWithCommas(vb.unverified)} sub="Users pending verification" />
					<MiniCard title="Blocked" value={formatWithCommas(vb.blocked)} sub="Blocked accounts" accent={COLORS.blocked} />
					<MiniCard
						title="Total Transaction Volume"
						value={`₦${formatCompactNumber(transactionStats.totalVolume)}`}
						sub={`${formatWithCommas(transactionStats.totalTransactions)} transactions`}
					/>
				</CardsGrid>

				{/* Section 2 — Donut Chart */}
				<SectionTitle>How Users Are Verified</SectionTitle>
				<Card>
					<ResponsiveContainer width="100%" height={260}>
						<PieChart>
							<Pie
								data={donutData}
								cx="50%"
								cy="50%"
								innerRadius={70}
								outerRadius={110}
								paddingAngle={3}
								dataKey="value"
							>
								{donutData.map((entry, i) => (
									<Cell key={i} fill={entry.color} />
								))}
							</Pie>
							<Tooltip
								formatter={(value, name) => [
									`${formatWithCommas(Number(value))} (${pct(Number(value), vb.total)}%)`,
									name,
								]}
							/>
						</PieChart>
					</ResponsiveContainer>
					<div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-2">
						{donutData.map(d => (
							<div key={d.name} className="flex items-center gap-1.5 text-xs text-[#6D6D72]">
								<span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: d.color }} />
								<span>{d.name}</span>
								<span className="font-medium text-[#101018]">{formatWithCommas(d.value)}</span>
								<span className="text-[#B3B3B6]">({pct(d.value, vb.total)}%)</span>
							</div>
						))}
					</div>
				</Card>

				{/* Section 3 — Horizontal Bar: Step Completion */}
				<SectionTitle>Verification Step Completion</SectionTitle>
				<Card>
					<ResponsiveContainer width="100%" height={220}>
						<BarChart data={stepData} layout="vertical" margin={{ left: 16, right: 40 }}>
							<CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F0F0F2" />
							<XAxis type="number" tick={{ fontSize: 11, fill: '#9C9C9F' }} tickFormatter={v => formatCompactNumber(v)} />
							<YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6D6D72' }} width={100} />
							<Tooltip
								formatter={(value) => [
									`${formatWithCommas(Number(value))} (${pct(Number(value), vb.total)}%)`,
									'Count',
								]}
							/>
							<Bar dataKey="count" fill="#761BB8" radius={[0, 4, 4, 0]}>
								<LabelList
									dataKey="count"
									position="right"
									formatter={(v: unknown) => `${pct(Number(v), vb.total)}%`}
									style={{ fontSize: 10, fill: '#9C9C9F' }}
								/>
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</Card>

				{/* Section 4 — Funnel */}
				<SectionTitle>Verification Funnel — Where Users Drop Off</SectionTitle>
				<Card>
					<ResponsiveContainer width="100%" height={300}>
						<FunnelChart>
							<Tooltip
								formatter={(value, _name, props) => [
									`${formatWithCommas(Number(value))} users — ${(props.payload as { dropOffRate?: string })?.dropOffRate ?? '0'}% drop-off`,
									'',
								]}
							/>
							<Funnel
								dataKey="count"
								data={verificationFunnel.map(f => ({
									...f,
									fill: parseFloat(f.dropOffRate) === maxDropOff && maxDropOff > 0 ? '#f97316' : '#761BB8',
								}))}
								isAnimationActive
							>
								<LabelList
									position="right"
									content={({ value, x, y, width, height, index }) => {
										const item = verificationFunnel[index as number];
										if (!item) return null;
										return (
											<text
												x={(x as number) + (width as number) + 8}
												y={(y as number) + (height as number) / 2 + 4}
												fontSize={11}
												fill="#6D6D72"
											>
												{item.step} · {formatWithCommas(Number(value))}
											</text>
										);
									}}
								/>
							</Funnel>
						</FunnelChart>
					</ResponsiveContainer>
					<p className="text-xs text-[#9C9C9F] mt-2">
						<span className="inline-block w-2.5 h-2.5 rounded-full bg-[#f97316] mr-1.5" />
						Highlighted step has the highest drop-off rate
					</p>
				</Card>

				{/* Section 5 — Line Chart */}
				<SectionTitle>Monthly Registrations: Verified vs Unverified</SectionTitle>
				<Card>
					<ResponsiveContainer width="100%" height={260}>
						<LineChart data={trendData} margin={{ left: 0, right: 16 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#F0F0F2" />
							<XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9C9C9F' }} />
							<YAxis tick={{ fontSize: 11, fill: '#9C9C9F' }} tickFormatter={v => formatCompactNumber(v)} />
							<Tooltip formatter={(v) => formatWithCommas(Number(v))} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							<Line type="monotone" dataKey="verified" stroke={COLORS.verified} strokeWidth={2} dot={false} name="Verified" />
							<Line type="monotone" dataKey="unverified" stroke={COLORS.unverified} strokeWidth={2} dot={false} name="Unverified" />
						</LineChart>
					</ResponsiveContainer>
				</Card>

				{/* Section 6 — Transactions by Verification Status */}
				<SectionTitle>Transactions: Verified vs Unverified Users</SectionTitle>
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
					<div className="flex flex-col gap-4">
						<Card>
							<StatTitle>Verified Users</StatTitle>
							<StatValue style={{ fontSize: 24 }}>
								{formatWithCommas(transactionStats.byVerificationStatus.verifiedUsers.count)}
							</StatValue>
							<MetaMuted change={false}>
								₦{formatCompactNumber(transactionStats.byVerificationStatus.verifiedUsers.volume)} volume
							</MetaMuted>
						</Card>
						<Card>
							<StatTitle>Unverified Users</StatTitle>
							<StatValue style={{ fontSize: 24 }}>
								{formatWithCommas(transactionStats.byVerificationStatus.unverifiedUsers.count)}
							</StatValue>
							<MetaMuted change={false}>
								₦{formatCompactNumber(transactionStats.byVerificationStatus.unverifiedUsers.volume)} volume
							</MetaMuted>
						</Card>
					</div>
					<Card>
						<ResponsiveContainer width="100%" height={200}>
							<BarChart data={txStatusData} margin={{ left: 0, right: 8 }}>
								<CartesianGrid strokeDasharray="3 3" stroke="#F0F0F2" />
								<XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9C9C9F' }} />
								<YAxis tick={{ fontSize: 11, fill: '#9C9C9F' }} tickFormatter={v => formatCompactNumber(v)} yAxisId="left" />
								<YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#9C9C9F' }} tickFormatter={v => formatCompactNumber(v)} />
								<Tooltip
									formatter={(v, name) => [
										name === 'count' ? formatWithCommas(Number(v)) : `₦${formatCompactNumber(Number(v))}`,
										name === 'count' ? 'Transactions' : 'Volume',
									]}
								/>
								<Legend wrapperStyle={{ fontSize: 12 }} />
								<Bar yAxisId="left" dataKey="count" name="count" fill="#761BB8" radius={[4, 4, 0, 0]}>
									{txStatusData.map((entry, i) => (
										<Cell key={i} fill={entry.name === 'Verified' ? COLORS.verified : COLORS.unverified} />
									))}
								</Bar>
								<Bar yAxisId="right" dataKey="volume" name="volume" fill="#3b82f6" radius={[4, 4, 0, 0]} opacity={0.6} />
							</BarChart>
						</ResponsiveContainer>
					</Card>
				</div>

				{/* Section 7 — Transaction Volume by Type */}
				<SectionTitle>Transaction Volume by Type</SectionTitle>
				<Card>
					<ResponsiveContainer width="100%" height={240}>
						<BarChart data={txTypeData} margin={{ left: 0, right: 8 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#F0F0F2" />
							<XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9C9C9F' }} />
							<YAxis tick={{ fontSize: 11, fill: '#9C9C9F' }} tickFormatter={v => `₦${formatCompactNumber(v)}`} />
							<Tooltip
								formatter={(v, _name, props) => [
									`₦${formatCompactNumber(Number(v))} · ${formatWithCommas((props.payload as { count?: number })?.count ?? 0)} txns`,
									'Volume',
								]}
							/>
							<Bar dataKey="volume" fill="#761BB8" radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
				</Card>

				{/* Section 8 — Blocked Users */}
				<SectionTitle>Blocked Users</SectionTitle>
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
					<MiniCard title="Total Blocked" value={formatWithCommas(blockedStats.total)} accent={COLORS.blocked} />
					<MiniCard
						title="Blocked with BVN"
						value={formatWithCommas(blockedStats.hadBvnVerified)}
						sub={`${pct(blockedStats.hadBvnVerified, blockedStats.total)}% of blocked`}
					/>
					<MiniCard
						title="Blocked with NIN"
						value={formatWithCommas(blockedStats.hadNinVerified)}
						sub={`${pct(blockedStats.hadNinVerified, blockedStats.total)}% of blocked`}
					/>
				</div>
			</>
		);
	};

	return (
		<Dashboard title="Analytics / User Verification" icon="solar:chart-2-linear" active="analytics_verification">
			<Content>
				<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
					<div>
						<Heading>User Verification Analytics</Heading>
						<HeadingSub style={{ margin: 0 }}>Insights into how users verify on the platform</HeadingSub>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<input
							type="date"
							value={dateRange.startDate}
							onChange={e => setDateRange(p => ({ ...p, startDate: e.target.value }))}
							className="text-xs px-3 py-2 border border-[#E7E7E9] rounded-lg bg-white text-[#101018] focus:outline-none focus:border-[#761BB8]"
						/>
						<input
							type="date"
							value={dateRange.endDate}
							onChange={e => setDateRange(p => ({ ...p, endDate: e.target.value }))}
							className="text-xs px-3 py-2 border border-[#E7E7E9] rounded-lg bg-white text-[#101018] focus:outline-none focus:border-[#761BB8]"
						/>
						<button
							onClick={handleApply}
							className="text-xs px-4 py-2 bg-[#761BB8] text-white rounded-lg hover:bg-[#6317a0] transition-colors"
						>
							Apply
						</button>
						{data && (
							<button
								onClick={exportCSV}
								className="flex items-center gap-1.5 text-xs px-3 py-2 border border-[#E7E7E9] rounded-lg hover:bg-[#F9F9FA] transition-colors"
							>
								<Icon icon="solar:export-linear" width={13} />
								Export CSV
							</button>
						)}
					</div>
				</div>

				{renderContent()}
			</Content>
		</Dashboard>
	);
}
